import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  RefreshCcw,
  CheckCircle,
  ClipboardList,
  Building2,
  Clock,
  X,
  UserCheck,
  Filter
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  usePointageEmployeeOptions,
} from './pointageListPageUtils';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import { CustomSelect, CustomDatePicker } from '../../shared/components/custom';
import '../../shared/styles/GPSTrackingDatePicker.css';
import {
  formatPointageDate,
  SHIFT_TYPE_VALUES,
  SAUDI_DEFAULT_TIMETRACKING_CONFIG,
  SAUDI_PRAYER_NAMES,
  normalizeShiftType,
  matchesShiftType,
} from './pointageHelpers';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { facturationTableScrollClass } from '../facturation/components/FacturationListPageLayout';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';

/** Parse stored employees field (comma-separated employee IDs from /hr/employees) */
function parseEmployeeIdCsv(str) {
  if (!str || !String(str).trim()) return [];
  return String(str)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function resolveEmployeeDisplayName(employeeId, employeeOptions) {
  const id = String(employeeId);
  const opt = employeeOptions.find((o) => String(o.value) === id);
  if (opt) return opt.meta?.name || opt.label;
  return id;
}

function formatPlanningEmployeesDisplay(employeesCsv, employeeOptions) {
  const ids = parseEmployeeIdCsv(employeesCsv);
  if (ids.length === 0) return '';
  return ids.map((id) => resolveEmployeeDisplayName(id, employeeOptions)).join(', ');
}

const TeamPlanningPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.teamPlanningPage.${key}`, opts);
  const qawaem = isSaudiBrand();

  const getShiftLabel = (shift) => {
    const normalized = normalizeShiftType(shift);
    const map = {
      [SHIFT_TYPE_VALUES.day]: p('shift_day'),
      [SHIFT_TYPE_VALUES.morning]: p('shift_morning'),
      [SHIFT_TYPE_VALUES.evening]: p('shift_evening'),
      [SHIFT_TYPE_VALUES.night]: p('shift_night'),
      [SHIFT_TYPE_VALUES.rotating]: p('shift_rotating'),
      [SHIFT_TYPE_VALUES.ramadan]: p('shift_ramadan'),
      [SHIFT_TYPE_VALUES.weekend]: p('shift_weekend'),
    };
    return map[normalized] || shift;
  };

  const shiftSelectOptions = [
    { value: SHIFT_TYPE_VALUES.morning, label: p('shift_morning_hours') },
    { value: SHIFT_TYPE_VALUES.evening, label: p('shift_evening_hours') },
    { value: SHIFT_TYPE_VALUES.night, label: p('shift_night_hours') },
    { value: SHIFT_TYPE_VALUES.ramadan, label: p('shift_ramadan_hours') },
    { value: SHIFT_TYPE_VALUES.rotating, label: p('shift_rotating') },
  ];

  const getStatusLabel = (status) => {
    const map = {
      active: p('status_active'),
      completed: p('status_completed')
    };
    return map[status] || p('em_dash');
  };

  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  
  // Date range filter
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  // Filters (toolbar)
  const [shiftFilter, setShiftFilter] = useState('all');
  const [statusFilterSelect, setStatusFilterSelect] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  
  // Options Avancées (GPS pattern)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_schedules');
  const [quickFilter, setQuickFilter] = useState('all');
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  // Data
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    teams: 0,
    thisWeek: 0
  });
  const [formData, setFormData] = useState({
    teamName: '',
    shift: '',
    startDate: '',
    endDate: '',
    employees: '',
    notes: '',
    status: '',
    breakTime: '',
    hoursPerDay: '',
    scheduleNotes: ''
  });

  /** RH — live /hr/employees options (refreshes on HR_EMPLOYEES_REFRESH_EVENT) */
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { valueField: 'id' },
  );
  /** Resettable picker after each add (2-in-1 select) */
  const [employeePickerValue, setEmployeePickerValue] = useState('');

  const [formTab, setFormTab] = useState('general');

  const TEAM_PLANNING_FORM_FIELD_COUNT = 10;

  const teamPlanningFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_basic_info'), icon: UserCheck, iconClass: 'text-blue-400' },
      { id: 'details', label: p('section_schedule_details'), icon: Clock, iconClass: 'text-orange-400' },
    ],
  }, TEAM_PLANNING_FORM_FIELD_COUNT), [p]);
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchSchedules();
    fetchStats();
  }, [shiftFilter, statusFilterSelect, dateRange]);

  const addEmployeeFromPicker = (employeeId) => {
    const id = (employeeId != null ? String(employeeId) : '').trim();
    if (!id) {
      setEmployeePickerValue('');
      return;
    }
    const current = parseEmployeeIdCsv(formData.employees);
    if (current.includes(id)) {
      setEmployeePickerValue('');
      return;
    }
    setFormData((f) => ({
      ...f,
      employees: [...current, id].join(', '),
    }));
    setEmployeePickerValue('');
  };

  const removeEmployeeTag = (idToRemove) => {
    const next = parseEmployeeIdCsv(formData.employees).filter((id) => id !== idToRemove);
    setFormData((f) => ({ ...f, employees: next.join(', ') }));
  };

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const params = {};
      if (shiftFilter && shiftFilter !== 'all') params.shift = shiftFilter;
      if (statusFilterSelect && statusFilterSelect !== 'all') params.status = statusFilterSelect;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;

      const data = await localApiAdapter.get('/timetracking/planning', params);
      setSchedules(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await localApiAdapter.get('/timetracking/planning/stats');
      setStats({
        total: data.total || 0,
        active: data.active || 0,
        completed: data.completed || 0,
        teams: data.teams || 0,
        thisWeek: data.thisWeek || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.shift?.trim()) {
      alert(p('alert_shift_required'));
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      alert(p('alert_dates_required'));
      return;
    }
    try {
      setLoading(true);
      const payload = {
        team_name: formData.teamName,
        shift: formData.shift.trim(),
        start_date: formData.startDate,
        end_date: formData.endDate,
        employees: formData.employees || undefined,
        notes: formData.notes,
        status: formData.status?.trim() || undefined,
        break_time: formData.breakTime || undefined,
        hours_per_day: formData.hoursPerDay || undefined,
        schedule_notes: formData.scheduleNotes || undefined
      };

      if (selectedSchedule) {
        await localApiAdapter.put(`/timetracking/planning/${selectedSchedule.id}`, payload);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/timetracking/planning', payload);
        notifyCreated(showNotify, p('title'));
      }

      await fetchSchedules();
      await fetchStats();
      resetForm();
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert(p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (schedule) => {
    setSelectedSchedule(schedule);
    setEmployeePickerValue('');
    setFormData({
      teamName: schedule.team_name || schedule.teamName,
      shift: schedule.shift,
      startDate: schedule.start_date || schedule.startDate,
      endDate: schedule.end_date || schedule.endDate,
      employees: schedule.employees || '',
      notes: schedule.notes || '',
      status: schedule.status,
      breakTime: schedule.break_time || schedule.breakTime || '',
      hoursPerDay: schedule.hours_per_day || schedule.hoursPerDay || '',
      scheduleNotes: schedule.schedule_notes || schedule.scheduleNotes || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        setLoading(true);
        await localApiAdapter.delete(`/timetracking/planning/${id}`);
        notifyDeleted(showNotify, p('title'));
        await fetchSchedules();
        await fetchStats();
      } catch (error) {
        console.error('Error deleting schedule:', error);
        alert(p('alert_delete_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/timetracking/planning/${id}`, { status: newStatus });
      await fetchSchedules();
      await fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const clickableStatusOptions = useMemo(() => [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'completed', label: p('status_completed'), icon: Calendar, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
  ], [p]);

  const statusColumnFilterOptions = useMemo(
    () => clickableStatusOptions.map(({ value, label }) => ({ value, label })),
    [clickableStatusOptions],
  );

  const handleView = (schedule) => {
    setSelectedSchedule(schedule);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ teamName: '', shift: '', startDate: '', endDate: '', employees: '', notes: '', status: '', breakTime: '', hoursPerDay: '', scheduleNotes: '' });
    setEmployeePickerValue('');
    setSelectedSchedule(null);
    setFormTab('general');
    setShowModal(false);
  };

  const getShiftBadge = (shift) => {
    const normalized = normalizeShiftType(shift);
    const styles = {
      [SHIFT_TYPE_VALUES.morning]: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      [SHIFT_TYPE_VALUES.evening]: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      [SHIFT_TYPE_VALUES.night]: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      [SHIFT_TYPE_VALUES.ramadan]: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      [SHIFT_TYPE_VALUES.rotating]: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[normalized] || styles[SHIFT_TYPE_VALUES.morning]}`}>
        {getShiftLabel(shift)}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      'active': 'bg-green-500/20 text-green-300 border-green-500/30',
      'completed': 'bg-gray-500/20 text-gray-300 border-gray-500/30'
    };
    const labels = {
      active: p('status_active'),
      completed: p('status_completed')
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles['active']}`}>
        {getStatusLabel(status)}
      </span>
    );
  };

  // Main View Tabs (GPS pattern)
  const mainTabs = [
    { id: 'all_schedules', name: p('tab_all'), icon: ClipboardList },
    { id: 'active_only', name: p('tab_active'), icon: CheckCircle },
    { id: 'completed_only', name: p('tab_completed'), icon: Calendar }
  ];

  // Apply main view and quick filters
  const getDistributionData = () => {
    const activeCount = schedules.filter(s => s.status === 'active').length;
    const completedCount = schedules.filter(s => s.status === 'completed').length;
    const thisWeekCount = stats.thisWeek || 0;
    
    return [
      { name: p('dist_active'), count: activeCount, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      { name: p('dist_completed'), count: completedCount, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
      { name: p('dist_this_week'), count: thisWeekCount, color: 'bg-gradient-to-r from-orange-500 to-orange-600' }
    ];
  };

  const baseFilteredSchedules = useMemo(() => schedules.filter((schedule) => {
    const matchesStatus =
      statusFilterSelect === 'all' || schedule.status === statusFilterSelect;
    const matchesShift = shiftFilter === 'all' || matchesShiftType(schedule.shift, shiftFilter);
    const name = schedule.team_name || schedule.teamName;
    const matchesSearch =
      !searchTerm ||
      name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatPlanningEmployeesDisplay(schedule.employees, employeeOptions)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      schedule.employees?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schedule.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Main view mode filter
    if (mainViewMode === 'active_only' && schedule.status !== 'active') return false;
    if (mainViewMode === 'completed_only' && schedule.status !== 'completed') return false;
    
    // Quick filter
    if (quickFilter === 'morning' && !matchesShiftType(schedule.shift, SHIFT_TYPE_VALUES.morning)) return false;
    if (quickFilter === 'evening' && !matchesShiftType(schedule.shift, SHIFT_TYPE_VALUES.evening)) return false;
    if (quickFilter === 'night' && !matchesShiftType(schedule.shift, SHIFT_TYPE_VALUES.night)) return false;
    if (quickFilter === 'ramadan' && !matchesShiftType(schedule.shift, SHIFT_TYPE_VALUES.ramadan)) return false;
    
    return matchesStatus && matchesShift && matchesSearch;
  }), [schedules, statusFilterSelect, shiftFilter, searchTerm, mainViewMode, quickFilter, employeeOptions]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('teamName', (s) => s.team_name || s.teamName || ''),
    textColumnRule('shift', (s) => s.shift || ''),
    textColumnRule('startDate', (s) => formatPointageDate(s.start_date || s.startDate) || ''),
    textColumnRule('endDate', (s) => formatPointageDate(s.end_date || s.endDate) || ''),
    textColumnRule('employees', (s) => formatPlanningEmployeesDisplay(s.employees, employeeOptions) || String(s.employees ?? s.employee_count ?? '')),
    exactColumnRule('status', (s) => s.status || ''),
  ], [employeeOptions]);

  const filteredSchedules = useMemo(
    () => applyColumnFilters(baseFilteredSchedules, colFilters, columnFilterRules),
    [baseFilteredSchedules, colFilters, columnFilterRules],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredSchedules);

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) return;
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        setLoading(true);
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/timetracking/planning/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        await fetchSchedules();
        await fetchStats();
      } catch (error) {
        console.error('Error bulk deleting schedules:', error);
        notifyError(showNotify, p('alert_bulk_delete_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const planningKpiCards = [
    { title: p('kpi_total'), value: stats.total, icon: Users, color: 'from-blue-500 to-blue-600' },
    { title: p('kpi_active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { title: p('kpi_completed'), value: stats.completed, icon: ClipboardList, color: 'from-indigo-500 to-indigo-600' },
    { title: p('kpi_teams'), value: stats.teams, icon: Building2, color: 'from-purple-500 to-purple-600' },
    { title: p('kpi_this_week'), value: stats.thisWeek, icon: Calendar, color: 'from-orange-500 to-orange-600' }
  ];

  const POINTAGE_TABLE_COLUMNS = [
    { key: 'teamName', labelKey: 'col_team' },
    { key: 'shift', labelKey: 'col_shift' },
    { key: 'startDate', labelKey: 'col_start_date' },
    { key: 'endDate', labelKey: 'col_end_date' },
    { key: 'employees', labelKey: 'col_employees' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', label: t('common.actions'), required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  // ===== RENDER =====
  return (
    <div className="space-y-6 clients-container">
      {/* Header */}
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white">
            {p('title')}
            <button onClick={() => { fetchSchedules(); fetchStats(); }} className="refresh-button ml-4" title={p('refresh')}>
              <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">
            {p('subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({
            count: selectedRecords.length,
            onDelete: handleBulkDelete,
            label: `${t('common.delete')} (${selectedRecords.length})`,
            title: t('common.delete', { defaultValue: 'حذف' }),
          })}
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions 
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400' 
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={t('common.advanced_options')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          
          <button
            type="button"
            onClick={() => {
              setSelectedSchedule(null);
              setEmployeePickerValue('');
              setFormData({
                teamName: '',
                shift: '',
                startDate: '',
                endDate: '',
                employees: '',
                notes: '',
                status: '',
                breakTime: '',
                hoursPerDay: '',
                scheduleNotes: ''
              });
              setFormTab('general');
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_planning')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards — grille 5 × Facturiers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {planningKpiCards.slice(0, 4).map((stat, i) => (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group/card">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover/card:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover/card:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options Section (GPS Pattern) */}
      <motion.div
        initial={false}
        animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ overflow: "hidden" }}
      >
        <div className="space-y-4 pt-4">
          {/* Main View Tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setMainViewMode(tab.id);
                    setQuickFilter('all');
                    setStatusFilterSelect(pointageStatusFilterFromMainTab(tab.id, {
                      active_only: 'active',
                      completed_only: 'completed',
                    }));
                    setIsStatusFilterOpen(false);
                  }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    mainViewMode === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Filter Buttons */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20">
            <h3 className="text-white font-semibold mb-3 text-sm">{p('quick_filters_shifts')}</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', label: p('filter_all'), icon: ClipboardList },
                { id: 'morning', label: p('shift_morning'), icon: Calendar },
                { id: 'evening', label: p('shift_evening'), icon: Clock },
                { id: 'night', label: p('shift_night'), icon: Building2 },
                { id: 'ramadan', label: p('shift_ramadan'), icon: Clock },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setQuickFilter(filter.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                    quickFilter === filter.id
                      ? 'bg-blue-500/30 text-blue-200 border-2 border-blue-400'
                      : 'bg-white/5 text-white/70 border border-white/20 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <filter.icon className="h-4 w-4" />
                  <span className="text-sm">{filter.label}</span>
                </button>
              ))}
            </div>
            <p className="text-white/50 text-xs mt-3">{p('work_week_label')}</p>
            <p className="text-white/50 text-xs">{p('prayer_breaks_label')} ({SAUDI_PRAYER_NAMES.map((name) => p(`prayer_${name.toLowerCase()}`, { defaultValue: name })).join(' · ')})</p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20">
            <h3 className="text-white font-semibold mb-3 text-sm">{p('distribution_overview')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {getDistributionData().map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white/5 backdrop-blur-lg rounded-2xl p-4 border border-white/20 hover:bg-white/10 transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/60 text-xs font-medium mb-1">{item.name}</p>
                      <p className="text-2xl font-bold text-white">{item.count}</p>
                    </div>
                    <div className={`p-3 rounded-xl ${item.color}`}>
                      <ClipboardList className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Data Table with Integrated Header */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden shadow-xl shadow-black/10">

        

        

        {/* Table or Card View */}
        {viewMode === 'table' ? (
          // Table View
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })}
                  />
                  {isColVisible('teamName') && (
                    <TableColumnHeaderFilter columnKey="teamName" label={p('col_team')} value={colFilters.teamName ?? ''} onChange={(v) => setColFilter('teamName', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('shift') && (
                    <TableColumnHeaderFilter columnKey="shift" label={p('col_shift')} value={colFilters.shift ?? ''} onChange={(v) => setColFilter('shift', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('startDate') && (
                    <TableColumnHeaderFilter columnKey="startDate" label={p('col_start_date')} value={colFilters.startDate ?? ''} onChange={(v) => setColFilter('startDate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('endDate') && (
                    <TableColumnHeaderFilter columnKey="endDate" label={p('col_end_date')} value={colFilters.endDate ?? ''} onChange={(v) => setColFilter('endDate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('employees') && (
                    <TableColumnHeaderFilter columnKey="employees" label={p('col_employees')} value={colFilters.employees ?? ''} onChange={(v) => setColFilter('employees', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={POINTAGE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="bg-white/5">
                {filteredSchedules.length > 0 ? (
                  filteredSchedules.map((schedule) => (
                    <tr key={schedule.id} className="hover:bg-white/10 transition-colors border-b border-white/10">
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(schedule.id)}
                        onChange={() => handleSelectRecord(schedule.id)}
                      />
                      {isColVisible('teamName') && (
                        <td className="px-6 py-4 text-white font-semibold">{schedule.team_name || schedule.teamName}</td>
                      )}
                      {isColVisible('shift') && (
                        <td className="px-6 py-4">{getShiftBadge(schedule.shift)}</td>
                      )}
                      {isColVisible('startDate') && (
                        <td className="px-6 py-4 text-white/80">{formatPointageDate(schedule.start_date || schedule.startDate)}</td>
                      )}
                      {isColVisible('endDate') && (
                        <td className="px-6 py-4 text-white/80">{formatPointageDate(schedule.end_date || schedule.endDate)}</td>
                      )}
                      {isColVisible('employees') && (
                        <td className="px-6 py-4 text-white/80">
                          {formatPlanningEmployeesDisplay(schedule.employees, employeeOptions) || schedule.employees || '—'}
                        </td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={schedule.status}
                              recordId={schedule.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                            onView={() => handleView(schedule)}
                            onEdit={() => handleEdit(schedule)}
                            onDelete={() => handleDelete(schedule.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="px-6 py-12 text-center text-white/50">
                      {p('empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          // Card View
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredSchedules.map((schedule) => (
                <motion.div
                  key={schedule.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl shadow-black/10 hover:bg-white/15 transition-all duration-300 h-full min-w-0"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2 flex-wrap">
                        {getShiftBadge(schedule.shift)}
                        {getStatusBadge(schedule.status)}
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-1 truncate">{schedule.team_name || schedule.teamName}</h3>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(schedule)}
                        onEdit={() => handleEdit(schedule)}
                        onDelete={() => handleDelete(schedule.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('date_start_col')}</span>
                      <span className="text-white font-semibold">{formatPointageDate(schedule.start_date || schedule.startDate)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('date_end_col')}</span>
                      <span className="text-white font-semibold">{formatPointageDate(schedule.end_date || schedule.endDate)}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-2">
                      <span className="text-white/50 text-xs">{p('col_employees')}</span>
                      <span className="text-white font-semibold text-sm">
                        {formatPlanningEmployeesDisplay(schedule.employees, employeeOptions) || schedule.employees || '—'}
                      </span>
                    </div>
                  </div>

                  {schedule.notes && (
                    <div className="bg-white/5 rounded-lg p-3 mt-4">
                      <p className="text-white/50 text-xs mb-1">{p('notes')}</p>
                      <p className="text-white text-sm">{schedule.notes}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal — même coquille « Ajouter un Pointage » */}
      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={() => setShowModal(false)}
          title={selectedSchedule ? p('modal_edit_title') : p('modal_new_title')}
          icon={Calendar}
          onSubmit
          formId="team-planning-form"
          saveLabel={selectedSchedule ? p('update') : t('common.save')}
          maxWidth="1000px"
          testId="team-planning-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={teamPlanningFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="team-planning-form"
            onSubmit={handleSubmit}
            ariaLabel={selectedSchedule ? p('modal_edit_title') : p('modal_new_title')}
          >
            {pointageFormTabVisible(teamPlanningFormTabs, formTab, 'general') && (
              <div className="space-y-4">
                <div className={POINTAGE_FORM_GRID_CLASS}>
                  <div>
                    <label className={modalForm.label}>{<RequiredLabelText text={p('team_name')} />}</label>
                    <input
                      type="text"
                      required
                      value={formData.teamName}
                      onChange={(e) => setFormData({ ...formData, teamName: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder:text-white/40"
                      placeholder={p('team_name_placeholder')}
                    />
                  </div>
                  <div>
                    <label className={modalForm.label}>{<RequiredLabelText text={p('shift_required')} />}</label>
                    <CustomSelect
                      value={formData.shift}
                      onChange={(v) => setFormData({ ...formData, shift: v })}
                      options={shiftSelectOptions}
                      placeholder={p('shift_select_placeholder')}
                      searchable={true}
                      creatable={true}
                      autoSelect={false}
                      className="[&_button]:min-h-[42px] [&_button]:text-sm"
                    />
                  </div>
                  <div>
                    <label className={modalForm.label}>{t('common.status')}</label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(v) => setFormData({ ...formData, status: v })}
                      options={[
                        { value: 'active', label: p('status_active') },
                        { value: 'completed', label: p('status_completed') }
                      ]}
                      placeholder={t('common.status')}
                      searchable={true}
                      creatable={true}
                      autoSelect={false}
                      className="[&_button]:min-h-[42px] [&_button]:text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
            {pointageFormTabVisible(teamPlanningFormTabs, formTab, 'general') && (
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('date_start')} />}</label>
                  <CustomDatePicker
                    value={formData.startDate}
                    onChange={(v) => setFormData({ ...formData, startDate: v })}
                    placeholder={p('date_start')}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('date_end')} />}</label>
                  <CustomDatePicker
                    value={formData.endDate}
                    onChange={(v) => setFormData({ ...formData, endDate: v })}
                    placeholder={p('date_end')}
                    minDate={formData.startDate ? new Date(`${formData.startDate}T12:00:00`) : undefined}
                  />
                </div>
              </div>
            )}
            {pointageFormTabVisible(teamPlanningFormTabs, formTab, 'details') && (
              <div className="space-y-4">
                <div>
                  <label className={modalForm.label}>{p('col_employees')}</label>
                  <p className="text-white/40 text-xs mb-3">
                    {p('employees_help')}
                  </p>
                  {parseEmployeeIdCsv(formData.employees).length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {parseEmployeeIdCsv(formData.employees).map((empId, idx) => (
                        <span
                          key={`${empId}-${idx}`}
                          className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-lg bg-blue-500/20 border border-blue-500/35 text-white text-sm"
                        >
                          <span className="max-w-[220px] truncate">
                            {resolveEmployeeDisplayName(empId, employeeOptions)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeEmployeeTag(empId)}
                            className="p-1 rounded-md hover:bg-white/10 text-white/70 hover:text-white"
                            title={p('remove')}
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {employeeOptions.length === 0 && (
                    <p className="text-amber-300/90 text-xs mb-2">
                      {p('no_employees_hr')}
                    </p>
                  )}
                  <CustomSelect
                    value={employeePickerValue}
                    onChange={(v) => addEmployeeFromPicker(v)}
                    options={employeeOptions.filter(
                      (opt) => !parseEmployeeIdCsv(formData.employees).includes(String(opt.value))
                    )}
                    placeholder={p('employee_picker')}
                    searchable={true}
                    creatable={false}
                    autoSelect={false}
                    className="[&_button]:min-h-[42px] [&_button]:text-sm"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('notes')}</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none placeholder:text-white/40"
                    placeholder={p('notes_placeholder')}
                  />
                </div>
              </div>
            )}
            {pointageFormTabVisible(teamPlanningFormTabs, formTab, 'details') && (
              <div className="space-y-4">
                <div className={POINTAGE_FORM_GRID_CLASS}>
                  <div>
                    <label className={modalForm.label}>{p('break_time')}</label>
                    <input
                      type="number"
                      value={formData.breakTime || ''}
                      onChange={(e) => setFormData({ ...formData, breakTime: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder:text-white/40"
                      placeholder={String(SAUDI_DEFAULT_TIMETRACKING_CONFIG.lunchBreakDuration)}
                    />
                  </div>
                  <div>
                    <label className={modalForm.label}>{p('hours_per_day')}</label>
                    <input
                      type="number"
                      step="0.5"
                      value={formData.hoursPerDay || ''}
                      onChange={(e) => setFormData({ ...formData, hoursPerDay: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder:text-white/40"
                      placeholder={String(
                        matchesShiftType(formData.shift, SHIFT_TYPE_VALUES.ramadan)
                          ? SAUDI_DEFAULT_TIMETRACKING_CONFIG.ramadanDailyHours
                          : SAUDI_DEFAULT_TIMETRACKING_CONFIG.maxDailyHours
                      )}
                    />
                  </div>
                </div>
                <div>
                  <label className={modalForm.label}>{p('schedule_notes')}</label>
                  <textarea
                    value={formData.scheduleNotes || ''}
                    onChange={(e) => setFormData({ ...formData, scheduleNotes: e.target.value })}
                    rows={3}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none placeholder:text-white/40"
                    placeholder={p('schedule_notes_placeholder')}
                  />
                </div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}

      {/* View Modal */}
      {showViewModal && selectedSchedule && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="800px"
          testId="team-planning-view-modal"
        >
          <div className="space-y-4 space-y-4">
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{p('col_team')}</p>
                <p className="text-white font-semibold text-lg">{selectedSchedule.team_name || selectedSchedule.teamName}</p>
              </div>
              <div className={modalForm.viewCard}>
                <p className="text-white/60 text-sm mb-2">{p('shift')}</p>
                {getShiftBadge(selectedSchedule.shift)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('date_start_col')}</p>
                  <p className={modalForm.viewValue}>{formatPointageDate(selectedSchedule.start_date || selectedSchedule.startDate)}</p>
                </div>
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('date_end_col')}</p>
                  <p className={modalForm.viewValue}>{formatPointageDate(selectedSchedule.end_date || selectedSchedule.endDate)}</p>
                </div>
              </div>
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{p('col_employees')}</p>
                <p className="text-white text-sm">
                  {formatPlanningEmployeesDisplay(selectedSchedule.employees, employeeOptions) || selectedSchedule.employees || '—'}
                </p>
              </div>
              {selectedSchedule.notes && (
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('notes')}</p>
                  <p className="text-white text-sm">{selectedSchedule.notes}</p>
                </div>
              )}
              <div className={modalForm.viewCard}>
                <p className="text-white/60 text-sm mb-2">{t('common.status')}</p>
                {getStatusBadge(selectedSchedule.status)}
              </div>
              <button
                type="button"
                onClick={() => setShowViewModal(false)}
                className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/20 transition-all"
              >
                {t('common.close')}
              </button>
            </div>
        </QawaemErpViewModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default TeamPlanningPage;


